import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { forgetWritten, openRow, usePanePrefs, type IPanePrefs,
  useOpenRecord
} from '@/design';
import { listsApi } from '../api/lists-api';
import { oversightApi } from '../api/oversight-api';
import { participantsApi } from '../api/participants-api';
import { originState, type IListOriginItem, type IOriginRecheck, type ListOriginPage } from './participant-link';

/**
 * A list whose rows open another area's records (SR-530 — Reinstatement, Registrations, Search
 * results, the recycle bin), as every work page opens a row: beside the list — the record with **this** list as its
 * column, under the search and filters it was opened from (`IListOrigin`, `ListArea`) — or in a new
 * tab, by *Opens in*. `rows` are the rows drawn, the column's; `beside` opens one beside the list,
 * `open` follows *Opens in*; `names` tells a row's record link (its name) apart, so a table's
 * `RowNameClicks` makes its plain click follow *Opens in* too.
 */
export function useListOrigin(
  page: ListOriginPage,
  title: string,
  rows: readonly IListOriginItem[],
  recheck?: IOriginRecheck
) {
  // Beside the list as *Opens in* says — its column, or a sheet over this page (SR-530).
  const openRecord = useOpenRecord();
  const location = useLocation();
  const prefs = usePanePrefs();
  const back = `${location.pathname}${location.search}`;
  // This list, read again: what was dealt with beside it is what its rows now are.
  React.useEffect(() => forgetWritten(back), [back]);
  const state = React.useMemo(
    () => originState({ page, title, back, items: [...rows], ...(recheck ? { recheck } : {}) }),
    [page, title, back, rows, recheck]
  );
  const beside = (to: string) => openRecord(to, state);
  const open = (to: string) => openRow(prefs, to, () => beside(to));
  /** Whether a link is a row's record's — its name — for `RowNameClicks`' `accept`. */
  const names = (link: HTMLAnchorElement) =>
    rows.some((row) => {
      const at = row.to.split(/[?#]/)[0];
      return at === link.pathname || at.startsWith(`${link.pathname}/`) || link.pathname.startsWith(`${at}/`);
    });
  return { prefs, beside, open, names } satisfies {
    prefs: IPanePrefs;
    beside: (to: string) => void;
    open: (to: string) => void;
    names: (link: HTMLAnchorElement) => boolean;
  };
}

/** The Lists field of a row's participant. */
const PARTICIPANT = 'sys.participant_id';

/** The page a recheck reads: one record's rows, or a list's page to look for it in. */
const RECHECK_PAGE = 100;

/**
 * Whether the record `item` names is still on the list the column came from (SR-530), asked once
 * after a write on it — the list's own question for that one record: a Lists row by its
 * participant under the worklist's criteria, a registration run among the list's runs, a response of
 * the bin by its own status (a restore is the one write that takes it off). A failed
 * read answers "still listed": a row is never said done on no evidence.
 */
export async function stillListed(recheck: IOriginRecheck, item: IListOriginItem, signal?: AbortSignal): Promise<boolean> {
  try {
    if (recheck.kind === 'lists') {
      const participantId = item.key.split(':')[0];
      const answer = await listsApi.rows(
        {
          query: {
            survey: recheck.survey,
            criteria: {
              version: 1,
              op: 'and',
              // The worklist's criteria as a group of the query's root (no `version` of its own).
              children: [
                { op: recheck.criteria.op, children: recheck.criteria.children },
                { field: PARTICIPANT, op: 'eq', value: participantId }
              ]
            }
          },
          page: 1,
          pageSize: RECHECK_PAGE
        },
        signal
      );
      return answer.total > 0;
    }
    if (recheck.kind === 'bin') {
      return (await oversightApi.getResponse(item.key, signal)).body.status === 'recycled';
    }
    const answer = await participantsApi.listRegistrations({ ...recheck.query, page: 1, pageSize: RECHECK_PAGE }, signal);
    // Past the page read, absence proves nothing: still listed.
    return answer.items.some((run) => run.participant?.participantId === item.key) || answer.total > answer.items.length;
  } catch (error) {
    // Kept for whoever debugs it; the row stays as it is.
    console.error(error);
    return true;
  }
}
